import {
    getStreamBalance,
    getStreamDisplayName,
    getStreamStatus,
    shouldOpenStreamDetails,
} from 'src/utils/leadStreams';

import type { Stream } from 'src/services/LeadStreamsService/agent';

export type CompanionState = 'idle' | 'searching' | 'found' | 'thinking' | 'attention' | 'offline' | 'loading';
export type CompanionAction = { label: string; path: string };
export type CompanionReply = { text: string; actions?: CompanionAction[] };

export const streamPath = (stream: Stream) =>
    shouldOpenStreamDetails(stream) ? `/leadstream/${stream.id}` : '/leadstream';

export function streamSummary(streams: Stream[], available: boolean) {
    if (!available)
        return { state: 'offline' as const, label: 'Нет связи', text: 'Не удалось обновить состояние потоков.' };
    const active = streams.filter((s) => s.is_active && getStreamStatus(s).key === 'active');
    if (active.length)
        return {
            state: 'searching' as const,
            label: 'Ищу лиды',
            text:
                active.length === 1
                    ? `Поиск включён: ${getStreamDisplayName(active[0])}.`
                    : `Поиск включён в ${active.length} потоках.`,
        };
    if (streams.some((s) => getStreamStatus(s).key === 'ended'))
        return {
            state: 'attention' as const,
            label: 'Нужен пакет',
            text: 'Активных потоков нет. У одного из потоков закончился пакет или доступ.',
        };
    if (streams.some((s) => getStreamStatus(s).key === 'paused'))
        return {
            state: 'idle' as const,
            label: 'На паузе',
            text: 'Поиск на паузе. Возобновите нужный поток в его карточке.',
        };
    return {
        state: 'idle' as const,
        label: 'Я рядом',
        text: 'Расскажите, какие клиенты вам нужны, или настройте первый поток.',
    };
}

// Only a counter increase on a previously observed stream is a new delivery.
// New streams and the initial response seed the baseline; filtering the feed cannot trigger it.
export function observeDeliveries(previous: Map<number, number>, streams: Stream[]) {
    const next = new Map<number, number>();
    let added = 0;
    for (const stream of streams) {
        const count = Math.max(0, Number(stream.approved_deliveries) || 0);
        const old = previous.get(stream.id);
        if (old !== undefined && count > old) added += count - old;
        next.set(stream.id, Math.max(old ?? 0, count));
    }
    return { next, added };
}

export function answerFromCabinet(question: string, streams: Stream[], available: boolean): CompanionReply | null {
    const q = question.toLowerCase().replace(/ё/g, 'е');
    const actions = streams.slice(0, 4).map((s) => ({ label: getStreamDisplayName(s), path: streamPath(s) }));
    if (
        /статус|что.*(ищешь|делаешь|поток)|как.*(идет поиск|работают.*поток)|ищешь.*лид|работа[ею]т.*поток|мои потоки|нет лид|почему.*лид/.test(
            q
        )
    ) {
        const summary = streamSummary(streams, available);
        const lines = available
            ? streams.slice(0, 6).map((s) => `${getStreamDisplayName(s)} — ${getStreamStatus(s).label.toLowerCase()}.`)
            : [];
        return {
            text: [summary.text, ...lines].join('\n'),
            actions: available && actions.length ? actions : [{ label: 'Настроить поток', path: '/leadstream' }],
        };
    }
    if (/сколько.*(остал|баланс)|остаток|баланс.*поток/.test(q)) {
        if (!available) return { text: 'Баланс сейчас недоступен. Повторите обновление состояния.' };
        return {
            text: streams.length
                ? streams
                      .map(
                          (s) =>
                              `${getStreamDisplayName(s)} — ${
                                  s.has_active_access ? 'доступ активен' : `${getStreamBalance(s)} лидов осталось`
                              }.`
                      )
                      .join('\n')
                : 'Потоков пока нет.',
            actions,
        };
    }
    if (/^(открой|покажи|где).*(лид|запрос)/.test(q))
        return {
            text: 'Все полученные запросы — в ленте. Там можно выбрать поток и оценить лид.',
            actions: streams.length
                ? streams.map((s) => ({ label: getStreamDisplayName(s), path: `/feed?stream=${s.id}&from=companion` }))
                : [{ label: 'Открыть лиды', path: '/feed' }],
        };
    if (/как.*(создать|настроить|запустить)|новый поток/.test(q))
        return {
            text: 'Опишите услугу и нужных клиентов, уточните отбор на примерах, затем выберите пакет. Уже начатую настройку можно продолжить из меню.',
            actions: [{ label: 'Настроить поток', path: '/leadstream' }],
        };
    if (/пауза|приостанов|останови|возобнови/.test(q))
        return {
            text: 'Напишите «Поставь поток на паузу» или «Возобнови поток». Я покажу выбранный поток и попрошу подтвердить действие.',
            actions,
        };
    if (/докупить|пополн|пакет|оплат|подписк/.test(q))
        return {
            text: 'Лиды покупаются пакетами. Цена зависит от категории и объёма; точная сумма показана перед оплатой. Для пополнения откройте нужный поток.',
            actions: streams.length
                ? streams.map((s) => ({
                      label: getStreamDisplayName(s),
                      path: shouldOpenStreamDetails(s) ? `${streamPath(s)}#checkout` : streamPath(s),
                  }))
                : [{ label: 'Выбрать поток', path: '/leadstream' }],
        };
    if (/ии.?менедж|ai.?менедж/.test(q))
        return {
            text: 'ИИ-менеджер помогает обрабатывать обращения по вашему сценарию и базе знаний. Начните с его настройки: задайте продукт, цель диалога и правила передачи человеку.',
            actions: [{ label: 'Настроить ИИ-менеджера', path: '/ai-managers/new' }],
        };
    if (/поддержк|человек|менеджер|помоги/.test(q))
        return {
            text: 'Могу объяснить настройку потока или показать его состояние. С вопросами по оплате и аккаунту поможет команда — кнопка поддержки внизу.',
        };
    return null;
}
